import React from 'react';
import { Box, Typography, Button } from '@mui/material';

import THEME from '../../constants/theme';

/**
 * ErrorBoundary component to catch and handle JavaScript errors in child components
 * Provides a fallback UI and error recovery options
 */
class ErrorBoundary extends React.Component {
  /**
   * Creates an instance of ErrorBoundary
   * @param {Object} props - Component props
   */
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  /**
   * Updates state when an error is caught
   * @param {Error} error - The error that was thrown
   * @returns {Object} Updated state object
   */
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  /**
   * Logs error information when component catches an error
   * @param {Error} error - The error that was thrown
   * @param {Object} errorInfo - Object with componentStack key
   */
  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    if (process.env.NODE_ENV === 'production') {
      // In production, log to error tracking service
      // Example: sendToErrorTrackingService(error, errorInfo);
    }
  }

  /**
   * Resets the error boundary state to retry rendering
   */
  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            padding: THEME.SPACING.LARGE,
            backgroundColor: THEME.COLORS.NEUTRAL_WHITE_LIGHT,
            borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
            margin: THEME.SPACING.LARGE
          }}
        >
          <Typography
            variant='h4'
            component='h1'
            sx={{
              color: THEME.COLORS.NEUTRAL_BLACK,
              marginBottom: THEME.SPACING.LARGE,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD
            }}
          >
            Something went wrong
          </Typography>

          <Typography
            variant='body1'
            sx={{
              marginBottom: THEME.SPACING.LARGE,
              textAlign: 'center',
              maxWidth: '600px',
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY
            }}
          >
            We apologize for the inconvenience. An error has occurred in this
            section of the page.
          </Typography>

          <Box sx={{ display: 'flex', gap: THEME.SPACING.MEDIUM }}>
            <Button
              onClick={this.handleReset}
              variant='contained'
              sx={{
                backgroundColor: THEME.COLORS.PRIMARY_BLUE,
                '&:hover': {
                  backgroundColor: THEME.COLORS.PRIMARY_BLUE_DARK
                },
                color: THEME.COLORS.NEUTRAL_WHITE,
                textTransform: 'none',
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
              }}
            >
              Try Again
            </Button>

            <Button
              onClick={() => window.location.reload()}
              variant='outlined'
              sx={{
                borderColor: THEME.COLORS.PRIMARY_BLUE,
                color: THEME.COLORS.PRIMARY_BLUE,
                '&:hover': {
                  borderColor: THEME.COLORS.PRIMARY_BLUE_DARK,
                  backgroundColor: 'transparent'
                },
                textTransform: 'none',
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
              }}
            >
              Refresh Page
            </Button>
          </Box>

          {process.env.NODE_ENV === 'development' && (
            <Box
              sx={{
                marginTop: THEME.SPACING.XL,
                padding: THEME.SPACING.MEDIUM,
                backgroundColor: THEME.COLORS.NEUTRAL_BLACK_LIGHTER,
                color: THEME.COLORS.NEUTRAL_WHITE,
                borderRadius: THEME.BORDERS.RADIUS.SMALL,
                width: '100%',
                maxWidth: '800px',
                overflow: 'auto'
              }}
            >
              <Typography
                variant='subtitle2'
                sx={{ fontWeight: 'bold', marginBottom: 1 }}
              >
                Technical details:
              </Typography>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                {this.state.error && this.state.error.toString()}
                <br />
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
